import * as React from 'react';
import * as elementResizeDetectorMaker from 'element-resize-detector';

import { StyledCustomMasonry, MasonryColumn } from './styles';
import { Format } from '../../utils/styles-constants';

interface CustomMasonryProps {
	children: React.ReactElement<{}>[];
}

interface CustomMasonryState {
	masonryArray: CustomMasonryItem[][];
}

interface CustomMasonryItem {
	index: number;
	element: React.ReactNode;
}

interface Size {
	width: number;
	height: number;
}

class CustomMasonry extends React.Component<CustomMasonryProps, CustomMasonryState> {
	private columnNb: number;
	private mainContainer: HTMLElement | null;
	private resizeDetector: elementResizeDetectorMaker.Erd;
	private containerSize: Size;
	private masonryItems: CustomMasonryItem[];
	private htmlElements: Array<HTMLElement | null>;
	private isHtmlInitialized: boolean;
	private isResizeDetectorInitialized: boolean;

	constructor(props: CustomMasonryProps) {
		super(props);
		this.state = { masonryArray: [] };
		this.columnNb = this.getColumnNb();
		this.mainContainer = null;
		this.isResizeDetectorInitialized = false;
		this.containerSize = { width: 0, height: 0 };
	}

	componentDidMount() {
		this.initializeResizeListener();
		this.initializeComponent();
		this.updateChildrenRendering(this.props);
	}

	componentWillReceiveProps(nextProps: CustomMasonryProps) {
		this.initializeResizeListener();

		if (this.props.children !== nextProps.children) {
			const oldChildrenKeys = this.props.children.map(item => item.key);
			const newChildrenKeys = nextProps.children.map(item => item.key);

			if (oldChildrenKeys.toString() !== newChildrenKeys.toString()) {
				this.initializeComponent();
			}
			this.updateChildrenRendering(nextProps);
		}
	}

	componentWillUnmount() {
		if (this.mainContainer) {
			this.resizeDetector.uninstall(this.mainContainer);
		}
	}

	render() {
		const { masonryArray } = this.state;

		return (
			<StyledCustomMasonry
				ref={(element: HTMLElement) => { this.mainContainer = element; }}
			>
				{masonryArray.map((column, columnIndex) => (
					<MasonryColumn key={columnIndex}>
						{column.map((item, itemIndex) => (
							<div
								key={itemIndex}
								ref={element => { this.htmlElements[item.index] = element; }}
							>
								{item.element}
							</div>
						))}
					</MasonryColumn>
				))}
			</StyledCustomMasonry>
		);
	}

	private initializeComponent = () => {
		this.htmlElements = [];
		this.isHtmlInitialized = false;
	}

	private updateChildrenRendering = ({ children }: CustomMasonryProps) => {
		this.masonryItems = children.map((element, index) => ({ element, index }));
		this.setState({
			masonryArray: this.getMasonryArray(this.columnNb),
		});
	}

	private initializeResizeListener = () => {
		if (!this.isResizeDetectorInitialized && this.mainContainer) {
			this.resizeDetector = elementResizeDetectorMaker({ strategy: 'object' });
			this.resizeDetector.listenTo(this.mainContainer, () => this.handleResize());
			this.isResizeDetectorInitialized = true;
		}
	}

	private getColumnNb = (): number => {
		if (window.matchMedia(Format.SmartphoneLandscape).matches || window.matchMedia(Format.Tablet).matches) {
			return 2;
		} else if (window.matchMedia(Format.SmallDesktop).matches) {
			return 3;
		} else if (window.matchMedia(Format.LargeDesktop).matches) {
			return 4;
		}
		return 1;
	}


	private getItemHeight = (item: CustomMasonryItem): number => {
		const htmlElement = this.htmlElements[item.index];
		return htmlElement ? htmlElement.offsetHeight : 1;
	}

	private handleResize = (): void => {
		const columnNb = this.getColumnNb();
		const columnNbHasChanged = this.columnNb !== columnNb;
		const newContainerSize = {
			width: this.mainContainer ? this.mainContainer.offsetWidth : 0,
			height: this.mainContainer ? this.mainContainer.offsetHeight : 0
		};
		const hasOnlyHeightChanged = newContainerSize.height !== this.containerSize.height && newContainerSize.width === this.containerSize.width;
		this.containerSize = newContainerSize;

		if (this.isHtmlInitialized && !hasOnlyHeightChanged && !columnNbHasChanged) {
			return;
		}

		this.isHtmlInitialized = (this.htmlElements && this.htmlElements.length > 0) || this.masonryItems.length === 0;
		this.columnNb = columnNb;
		const masonryArray = this.getMasonryArray(columnNb);
		this.setState({ masonryArray });
	}

	private getMasonryArray = (columnNb: number): CustomMasonryItem[][] => {
		let columnHeights: number[] = [];
		let masonryArray: CustomMasonryItem[][] = [];

		for (let i = 0; i < columnNb; i++) {
			columnHeights.push(0);
			masonryArray.push([]);
		}

		this.masonryItems.forEach(item => {
			const itemHeight = this.getItemHeight(item);
			let minColumn = 0;

			columnHeights.forEach((columnHeight, columnIndex) => {
				if (columnHeight < columnHeights[minColumn]) {
					minColumn = columnIndex;
				}
			});

			masonryArray[minColumn].push(item);
			columnHeights[minColumn] += itemHeight;
		});

		return masonryArray;
	}
}

export default CustomMasonry;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/CustomMasonry/index.tsx